import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, Share2, BookOpen, CheckCircle } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import { programs } from '../data/programs';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import BlogVisual from '../components/BlogVisual';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('');

  // Función para procesar markdown básico en el contenido
  const processMarkdown = (text) => {
    if (!text) return text;
    
    // Primero convertir **texto** a marcadores temporales para evitar conflictos
    let processed = text.replace(/\*\*(.+?)\*\*/g, '___STRONG_START___$1___STRONG_END___');
    
    // Luego convertir *texto* restantes a <em>
    processed = processed.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    // Finalmente convertir marcadores temporales a <strong>
    processed = processed.replace(/___STRONG_START___/g, '<strong>');
    processed = processed.replace(/___STRONG_END___/g, '</strong>');
    
    return processed;
  };

  // Encontrar el post actual
  const post = blogPosts.find(p => p.slug === slug);

  // Posts relacionados (misma categoría, excluyendo el actual)
  const relatedPosts = post 
    ? blogPosts
        .filter(p => p.category === post.category && p.slug !== post.slug)
        .slice(0, 3)
    : [];

  // Si no existe el post, redirigir a 404
  useEffect(() => {
    if (!post) {
      navigate('/404');
    }
  }, [post, navigate]);

  // Scroll tracking para tabla de contenidos
  useEffect(() => {
    if (!post) return;

    const handleScroll = () => {
      const sections = post.sections || [];
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  // Función para compartir
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copiar URL
      navigator.clipboard.writeText(window.location.href);
      alert('Enlace copiado al portapapeles');
    }
  };

  if (!post) return null;

  // 🆕 FASE 7: Article Schema para SEO (BreadcrumbList + Article con image requerida)
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "name": "Breadcrumbs",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://edutechpulselab.com" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://edutechpulselab.com/blog" },
          { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://edutechpulselab.com/blog/${post.slug}` }
        ]
      },
      {
        "@type": "Article",
        "headline": post.title,
        "description": post.excerpt,
        "image": {
          "@type": "ImageObject",
          "url": "https://edutechpulselab.com/isotipoedutech.png",
          "width": 800,
          "height": 800
        },
        "author": {
          "@type": "Person",
          "name": post.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Edutech Pulse Lab",
          "logo": {
            "@type": "ImageObject",
            "url": "https://edutechpulselab.com/isotipoedutech.png"
          }
        },
        "datePublished": post.publishedDate,
        "dateModified": post.publishedDate,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://edutechpulselab.com/blog/${post.slug}`
        },
        "keywords": post.keywords.join(', ')
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <SEOHead 
        title={`${post.title} | Blog Edutech Pulse Lab`}
        description={post.excerpt}
        keywords={post.keywords.join(', ')}
        canonicalUrl={`/blog/${post.slug}`}
        type="article"
        schema={articleSchema}
        image={post.ogImage || `https://edutechpulselab.com/isotipoedutech.png`}
      />

      <Navbar />

      <section className="bg-gradient-to-br from-[#0c203b] via-[#1a365d] to-[#2d4a6b] text-white py-10 sm:py-12 lg:py-16">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al blog
          </Link>

          {/* Categoría */}
          <div className="mb-4">
            <span className="px-3 sm:px-4 py-1 sm:py-1.5 border border-white/30 text-white/90 text-xs sm:text-sm font-medium rounded">
              {post.category}
            </span>
          </div>

          {/* Título */}
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight tracking-tight">
            {post.title}
          </h1>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-white/70 mb-6 pb-6 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{new Date(post.publishedDate).toLocaleDateString('es-ES', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{post.readTime}</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Por {post.author}</span>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 ml-auto text-white/70 hover:text-white transition-colors"
            >
              <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Compartir</span>
            </button>
          </div>
        </div>
      </section>

      {/* Contenido principal con sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar - Tabla de contenidos (solo desktop) */}
          {post.sections && post.sections.length > 0 && (
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-24 p-4">
                <h3 className="text-slate-400 font-medium mb-4 text-xs uppercase tracking-widest">
                  Contenido
                </h3>
                <nav className="space-y-1">
                  {post.sections.filter(s => !s.type).map((section, index) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className={`block text-xs py-1.5 px-2 rounded transition-colors ${
                        activeSection === section.id
                          ? 'text-slate-700 font-medium'
                          : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      {index + 1}. {section.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          {/* Artículo */}
          <article className="lg:col-span-3">
            <div className="prose prose-sm sm:prose-slate sm:prose-base max-w-none">
              {/* Introducción */}
              <div className="bg-slate-50 rounded-xl p-5 sm:p-6 mb-8 sm:mb-10 shadow-sm">
                <p 
                  className="text-slate-700 text-sm sm:text-base leading-relaxed mb-0"
                  dangerouslySetInnerHTML={{ __html: processMarkdown(post.excerpt) }}
                />
              </div>

              {/* Contenido por secciones */}
              {post.sections && post.sections.map((section, sectionIndex) => {
                const displayNumber = post.sections.slice(0, sectionIndex + 1).filter(s => !s.type).length;

                if (section.type === 'hook') {
                  return (
                    <div key={section.id} id={section.id} className="mb-10 scroll-mt-24">
                      <div className="bg-[#0c203b] border-l-4 border-slate-400 rounded-sm p-6 sm:p-8 space-y-4">
                        {section.content.split('\n\n').map((para, idx) => (
                          <p key={idx}
                            className="text-white/90 text-sm sm:text-base leading-loose tracking-wide"
                            dangerouslySetInnerHTML={{ __html: processMarkdown(para).replace(/\n/g, '<br />') }}
                          />
                        ))}
                      </div>
                    </div>
                  );
                }

                if (section.type === 'cta') {
                  return (
                    <div key={section.id} id={section.id} className="mb-10 scroll-mt-24">
                      <div className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6">
                        <p className="text-[#0c203b] font-semibold mb-1.5 text-sm sm:text-base tracking-tight">{section.title}</p>
                        <p className="text-slate-500 text-sm mb-5 leading-relaxed">{section.content}</p>
                        <Link
                          to={section.ctaUrl}
                          className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start gap-2 px-5 py-3 bg-[#0c203b] hover:bg-slate-800 text-white font-medium rounded-sm transition-colors text-sm"
                        >
                          {section.ctaText}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  );
                }

                if (section.type === 'highlight') {
                  return (
                    <div key={section.id} id={section.id} className="mb-10 scroll-mt-24">
                      <div className="border-l-2 border-slate-300 pl-5 py-1 space-y-3">
                        {section.content.split('\n\n').map((para, idx) => (
                          <p key={idx}
                            className="text-slate-500 text-sm sm:text-base leading-relaxed italic"
                            dangerouslySetInnerHTML={{ __html: processMarkdown(para).replace(/\n/g, '<br />') }}
                          />
                        ))}
                      </div>
                    </div>
                  );
                }

                if (section.type === 'faq') {
                  return (
                    <div key={section.id} id={section.id} className="mb-10 scroll-mt-24">
                      <h2 className="text-xl sm:text-2xl font-semibold text-[#0c203b] mb-5">Preguntas frecuentes</h2>
                      <div className="space-y-4">
                        {section.faqs.map((item, idx) => (
                          <div key={idx} className="border border-slate-200 rounded-sm">
                            <dt className="px-5 py-4 text-sm font-semibold text-[#0c203b] leading-snug">{item.q}</dt>
                            <dd className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{item.a}</dd>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                if (section.type === 'scenario') {
                  return (
                    <div key={section.id} id={section.id} className="mb-10 scroll-mt-24">
                      <div className="bg-slate-50 border border-slate-200 rounded-sm p-5 sm:p-6">
                        <p className="text-slate-500 font-semibold text-xs uppercase tracking-widest mb-4">{section.title}</p>
                        <div className="space-y-2 mb-4">
                          {section.content.split('\n\n').map((para, idx) => (
                            <p key={idx}
                              className="text-slate-700 text-sm sm:text-base leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: processMarkdown(para).replace(/\n/g, '<br />') }}
                            />
                          ))}
                        </div>
                        {section.options && (
                          <ul className="space-y-1.5 mb-5 pl-2 border-l border-slate-300">
                            {section.options.map((opt, idx) => (
                              <li key={idx} className="text-slate-500 text-sm pl-3">{opt}</li>
                            ))}
                          </ul>
                        )}
                        {section.resolution && (
                          <p className="text-slate-600 text-sm font-medium mb-5 leading-relaxed border-t border-slate-200 pt-4">{section.resolution}</p>
                        )}
                        <Link
                          to={section.ctaUrl}
                          className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start gap-2 px-5 py-3 bg-[#0c203b] hover:bg-slate-800 text-white font-medium rounded-sm transition-colors text-sm"
                        >
                          {section.ctaText}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  );
                }

                return (
                  <section key={section.id} id={section.id} className="mb-10 sm:mb-12 scroll-mt-24">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="flex items-center justify-center w-7 h-7 border border-slate-300 text-slate-600 text-sm font-semibold rounded">
                        {displayNumber}
                      </span>
                      <h2 className="text-xl sm:text-2xl font-semibold text-[#0c203b]">
                        {section.title}
                      </h2>
                    </div>
                    {section.visual && (
                      <div className="my-6">
                        <BlogVisual type={section.visual} />
                      </div>
                    )}
                    <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
                      {section.content.split('\n\n').map((paragraph, idx) => (
                        <p 
                          key={idx} 
                          className="mb-4"
                          dangerouslySetInnerHTML={{ __html: processMarkdown(paragraph) }}
                        />
                      ))}
                    </div>

                    {/* Lista de puntos clave si existe */}
                    {section.keyPoints && section.keyPoints.length > 0 && (
                      <div className="bg-white rounded-lg p-4 sm:p-5 mt-6 shadow-sm">
                        <ul className="space-y-2.5">
                          {section.keyPoints.map((point, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-slate-700 text-sm">
                              <span className="text-[#0c203b] font-semibold mt-0.5">•</span>
                              <span dangerouslySetInnerHTML={{ __html: processMarkdown(point) }} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </section>
                );
              })}

              {/* Conclusión */}
              {post.conclusion && (
                <div className="border-t border-slate-200 pt-8 sm:pt-10 mt-10 sm:mt-12">
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#0c203b] mb-4">Conclusión</h2>
                  <p 
                    className="text-slate-700 leading-relaxed text-sm sm:text-base"
                    dangerouslySetInnerHTML={{ __html: processMarkdown(post.conclusion) }}
                  />
                </div>
              )}
            </div>

            {/* Tags */}
            {post.keywords && post.keywords.length > 0 && (
              <div className="mt-8 pt-8 border-t border-slate-200">
                <div className="flex flex-wrap gap-2">
                  {post.keywords.map((keyword) => (
                    <span 
                      key={keyword}
                      className="px-3 py-1 border border-slate-300 text-slate-600 text-xs rounded hover:border-slate-400 transition-colors"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Micro-Programas */}
            {/* 🆕 PROFUNDIZA CON PROGRAMA ESPECÍFICO - Enlazado interno estratégico */}
            <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 mt-10 sm:mt-12">
              <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">
                {(post.slug.includes('ventas') || post.category === 'Ventas')
                  ? 'Aquí es donde la mayoría pierde el deal'
                  : '¿Te gustó este artículo?'}
              </h3>
              
              {(post.slug.includes('ventas') || post.category === 'Ventas') ? (
                <>
                  <p className="text-slate-700 mb-4 text-sm sm:text-base leading-relaxed">
                    No en la demo. No en el discovery. La mayoría pierde el deal en el momento donde nadie quiere decidir. En <strong>Ventas consultivas: influir en decisiones B2B</strong> enfrentas esa converación en vivo.
                  </p>
                  <div className="flex flex-wrap gap-3 mb-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      45 min total
                    </span>
                    <span className="flex items-center gap-1.5">
                      <BookOpen className="w-4 h-4" />
                      3 conversaciones
                    </span>
                    <span className="bg-white px-2.5 py-1 rounded text-xs font-medium border border-slate-200">
                      Basado en Cialdini, Rackham, Dixon/Adamson
                    </span>
                  </div>
                  <Link
                    to="/simulador-b2b"
                    className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start gap-2 px-6 py-3.5 bg-[#0c203b] hover:bg-slate-800 text-white font-medium rounded-sm transition-colors text-sm sm:text-base"
                  >
                    Entrar al simulador
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-slate-600 mb-6 text-sm sm:text-base">
                    Profundiza en estos temas con nuestros micro-programas especializados. 
                    Aprende con metodologías prácticas y ejercicios con IA.
                  </p>
                  <Link
                    to="/programas"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#0c203b] text-white font-medium rounded-lg hover:bg-slate-800 transition-colors text-sm sm:text-base"
                  >
                    Explorar Micro-Programas
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              )}
            </div>
          </article>
        </div>
      </div>

      {/* Posts relacionados */}
      {relatedPosts.length > 0 && (
        <div className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0c203b] mb-6 sm:mb-8">Artículos Relacionados</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {relatedPosts.map((relatedPost) => (
                <article 
                  key={relatedPost.slug}
                  className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200 group"
                >
                  <span className="inline-block px-2.5 py-0.5 border border-slate-300 text-slate-600 text-xs font-medium rounded">
                    {relatedPost.category}
                  </span>
                  <Link to={`/blog/${relatedPost.slug}`}>
                    <h3 className="text-base sm:text-lg font-semibold text-[#0c203b] mt-3 mb-2 group-hover:text-slate-700 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                  </Link>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {relatedPost.excerpt}
                  </p>
                  <Link 
                    to={`/blog/${relatedPost.slug}`}
                    className="inline-flex items-center gap-2 text-[#0c203b] font-medium text-sm hover:gap-3 transition-all"
                  >
                    Leer más
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPost;
